import { useEscapeKey } from '@/hooks/useEscapeKey';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { type FormEvent, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Self-service změna vlastního hesla. Uživatel zadá stávající + 2× nové.
 * API zvalidovává sílu hesla (10+ znaků, alespoň jedno písmeno).
 *
 * Po úspěchu se ostatní sessions invalidují, aktuální cookie zůstává —
 * uživatel zůstane přihlášený, ale jiná zařízení/prohlížeče se odhlásí.
 */
export function ChangePasswordModal({
  onClose,
  onSuccess,
  onError,
}: {
  onClose: () => void;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}) {
  useEscapeKey(onClose);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setLocalError(null);
    if (newPassword !== confirmPassword) {
      setLocalError('Nové hesla se neshodují');
      return;
    }
    if (newPassword === currentPassword) {
      setLocalError('Nové heslo se musí lišit od stávajícího');
      return;
    }
    setSaving(true);
    try {
      await apiFetch('/api/auth/change-password', {
        method: 'POST',
        body: { current_password: currentPassword, new_password: newPassword },
      });
      onSuccess('Heslo změněno');
    } catch (e: unknown) {
      onError(e instanceof ApiError ? e.describe() : 'Změna hesla selhala');
    } finally {
      setSaving(false);
    }
  }

  // Sidebar je posunutý transformem a ten by „fixed" okno uvěznil v jeho
  // šířce — proto se modal vykresluje do <body>.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        onClick={onClose}
        aria-label="Zavřít"
        className="absolute inset-0 bg-black/30 backdrop-blur-sm cursor-default"
      />
      <div className="relative w-full max-w-md bg-surface rounded-xl p-6 shadow-modal reveal">
        <h2 className="text-lg font-bold text-text mb-1">Změna hesla</h2>
        <p className="text-xs text-text-muted mb-5">
          Ostatní přihlášení (jiný prohlížeč/zařízení) se po změně odhlásí.
        </p>
        <hr className="border-border mb-5" />

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label
              htmlFor="cp-current"
              className="text-sm font-medium text-text-secondary block mb-1.5"
            >
              Stávající heslo
            </label>
            <input
              id="cp-current"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              autoComplete="current-password"
              className="w-full text-sm text-text bg-bg border border-border rounded-lg px-3.5 py-2.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary-100 transition-all"
            />
          </div>
          <div>
            <label
              htmlFor="cp-new"
              className="text-sm font-medium text-text-secondary block mb-1.5"
            >
              Nové heslo
            </label>
            <input
              id="cp-new"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              autoComplete="new-password"
              minLength={10}
              className="w-full text-sm text-text bg-bg border border-border rounded-lg px-3.5 py-2.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary-100 transition-all"
            />
            <p className="text-xs text-text-muted mt-1.5">Min 10 znaků, alespoň 1 písmeno.</p>
          </div>
          <div>
            <label
              htmlFor="cp-confirm"
              className="text-sm font-medium text-text-secondary block mb-1.5"
            >
              Nové heslo znovu
            </label>
            <input
              id="cp-confirm"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              autoComplete="new-password"
              minLength={10}
              className="w-full text-sm text-text bg-bg border border-border rounded-lg px-3.5 py-2.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary-100 transition-all"
            />
          </div>

          {localError && (
            <div className="text-sm text-danger bg-danger-light border border-danger/30 rounded-lg px-3 py-2">
              {localError}
            </div>
          )}

          <div className="flex gap-3 mt-6">
            <div className="flex-1" />
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="text-sm font-semibold text-text-secondary border border-border rounded-lg px-4 py-2.5 hover:bg-surface-hover transition-colors cursor-pointer disabled:opacity-50"
            >
              Zrušit
            </button>
            <button
              type="submit"
              disabled={saving}
              className="bg-action hover:bg-action-dark text-white text-sm font-semibold rounded-lg px-6 py-2.5 transition-colors cursor-pointer disabled:opacity-50"
            >
              {saving ? 'Ukládám…' : 'Změnit heslo'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
